// PROA_TRIAL_30D_UNIFIED_V1
// PRC-021 · C2 (activando / tarda más de lo normal) y C3 (pago confirmado).
import { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../config/axiosInstance';
import { useAuth } from '../context/AuthContext';
import { SimpleLayout } from '../modules/pricingJourney/components/JourneyLayouts';
import { StateIcon, StatusBlock } from '../modules/pricingJourney/components/JourneyBlocks';
import { Eyebrow, JourneyButton, JourneyCard, JourneyChip, JourneyText } from '../modules/pricingJourney/components/JourneyBasics';
import { J } from '../modules/pricingJourney/journeyTokens';
import {
  CurrentSubscription, fetchCurrentSubscription, formatMoney, formatShortDate, intervalLabel, PLAN_LABEL, SUPPORT_EMAIL,
} from '../modules/pricingJourney/journeyBilling';

type CheckoutState = 'waiting' | 'confirmed' | 'timeout';

// 20 comprobaciones cada 2 s: pasados 40 s, la variante «Tu pago se está confirmando».
const POLL_MS = 2000;
const MAX_POLLS = 20;
const APP_HOME = '/margin-center/overview';

const KV: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <Box>
    <Box sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: J.muted }}>{k}</Box>
    <Box sx={{ fontSize: 15, fontWeight: 700, color: J.text, mt: '4px' }}>{v || '—'}</Box>
  </Box>
);

const PaymentCallbackPage = () => {
  const [state, setState] = useState<CheckoutState>('waiting');
  const [subscription, setSubscription] = useState<CurrentSubscription | null>(null);
  const navigate = useNavigate();
  const { refreshProfile, user, signOut } = useAuth();
  const [attempt, setAttempt] = useState(0);

  const confirm = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/auth/pricing-app-context');
      if (data?.access?.state === 'paid' && data?.access?.has_access === true) {
        await refreshProfile();
        try {
          setSubscription(await fetchCurrentSubscription());
        } catch (error) {
          console.warn('[PaymentCallback] No se pudieron leer los datos de la suscripción', error);
        }
        setState('confirmed');
        return true;
      }
    } catch (error) {
      console.warn('[PaymentCallback] Esperando confirmacion segura del backend', error);
    }
    return false;
  }, [refreshProfile]);

  useEffect(() => {
    if (state !== 'waiting') return;
    let cancelled = false;
    let count = 0;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      if (cancelled) return;
      const confirmed = await confirm();
      if (cancelled || confirmed) return;
      count += 1;
      if (count >= MAX_POLLS) {
        setState('timeout');
        return;
      }
      timeoutHandle = setTimeout(poll, POLL_MS);
    };
    void poll();
    return () => { cancelled = true; if (timeoutHandle) clearTimeout(timeoutHandle); };
  }, [confirm, attempt, state]);

  const userLabel = ((user as any)?.full_name as string | undefined) || user?.email || null;
  const planName = subscription?.plan ? PLAN_LABEL[subscription.plan] : '';
  const period = intervalLabel(subscription?.interval);
  const invoiceHref = subscription?.invoice_pdf || subscription?.invoice_url;

  return (
    <SimpleLayout userLabel={userLabel} onSignOut={() => { void signOut(); }} narrow>
      {state === 'waiting' && (
        <StatusBlock
          variant="loading"
          minHeight={440}
          chip={<JourneyChip>Normalmente, menos de 10 segundos</JourneyChip>}
          title="Activando tu plan"
          text="Pago recibido. Estamos activando tu suscripción. No cierres esta ventana."
        />
      )}
      {state === 'timeout' && (
        <StatusBlock
          variant="warning"
          minHeight={440}
          title="Tu pago se está confirmando"
          text={<>Stripe ya tiene tu pago, pero la activación está tardando más de lo normal. <b style={{ color: J.text }}>No vuelvas a pagar:</b> el plan se activará solo en unos minutos.</>}
          actions={
            <>
              <JourneyButton small onClick={() => { setState('waiting'); setAttempt((x) => x + 1); }}>Comprobar de nuevo</JourneyButton>
              <JourneyButton kind="ghost" small onClick={() => navigate('/subscription')}>Ir a Mi suscripción</JourneyButton>
            </>
          }
          footnote={`Si en 15 minutos sigue igual, escríbenos a ${SUPPORT_EMAIL}.`}
        />
      )}
      {state === 'confirmed' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '24px' }}>
          <StateIcon tone="ok"><CheckCircleOutlineIcon /></StateIcon>
          <Eyebrow both>Suscripción activa</Eyebrow>
          <JourneyText variant="title">Ya eres cliente de PROA Pricing</JourneyText>
          <JourneyText variant="lead">
            Tu plan <b style={{ color: J.text }}>{[planName, period].filter(Boolean).join(' ') || 'de PROA Pricing'}</b> está activo. Tus datos y lo que has preparado en la prueba siguen exactamente donde estaban.
          </JourneyText>
          {subscription?.has_subscription && (
            <JourneyCard sx={{ width: '100%', textAlign: 'left' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '16px' }}>
                <KV k="Plan" v={[planName, period].filter(Boolean).join(' · ')} />
                <KV k="Próxima renovación" v={formatShortDate(subscription.current_period_end)} />
                {/* Lo cobrado de verdad (con cupón puede ser 0); si no hay factura, el precio. */}
                <KV k="Importe" v={(subscription.last_charge_amount ?? subscription.amount) != null ? `${formatMoney(subscription.last_charge_amount ?? subscription.amount, subscription.currency)} + IVA` : ''} />
                <KV k="Factura" v={invoiceHref ? <Box component="a" href={invoiceHref} target="_blank" rel="noopener noreferrer" sx={{ color: J.skyText, textDecoration: 'none' }}>Descargar</Box> : ''} />
              </Box>
            </JourneyCard>
          )}
          <JourneyCard tight sx={{ width: '100%', textAlign: 'left', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <JourneyText variant="h3">Ya puedes invitar a tu equipo</JourneyText>
              {subscription?.users_max ? <JourneyText variant="small">Hasta {subscription.users_max} usuarios en {planName || 'tu plan'}.</JourneyText> : null}
            </Box>
            <JourneyButton kind="ghost" small onClick={() => navigate('/team')}>Invitar al equipo</JourneyButton>
          </JourneyCard>
          <JourneyButton sx={{ minWidth: 260 }} onClick={() => navigate(APP_HOME)}>Ir a la aplicación</JourneyButton>
        </Box>
      )}
    </SimpleLayout>
  );
};

export default PaymentCallbackPage;
